// outpost-crm-ui/src/Chairs.jsx
// Outpost Evacuation chair-tracking module — isolated from rest of CRM.
// All chair UI lives here. App.js imports ChairsScreen and renders it.
// Future: lift this file + outpost_crm/chairs.py + chairs_pdf.py to a
// standalone repo without touching the rest of the codebase.

import { useState, useEffect, useCallback, useRef } from "react";
import ChairsMap from "./ChairsMap";
import { loadGoogleMaps } from "./googleMapsLoader";

// Brand palette — local so this module is self-contained.
const C = {
  green:       "#494C43",
  greenDark:   "#33362E",
  orange:      "#D66F27",
  orangeLight: "#E8843A",
  white:       "#FFFFFF",
  cream:       "#FAF9F5",
  card:        "#FFFFFF",
  border:      "#E5E5E0",
  light:       "#F0EFEA",
  grayMid:     "#9A9B96",
  grayDark:    "#5C5E57",
  success:     "#4CAF50",
  warning:     "#FFC107",
  danger:      "#F44336",
};

const ADMIN_EMAIL = "drew@outpostprep.com";
const CRM_URL = "https://outpost-crm-production.up.railway.app";
const CHAIR_MODELS = ["swift", "power", "lift", "mini", "hybrid"];
const CHAIR_STATUSES = ["inventory", "in_place", "moved", "missing", "maintenance", "retired"];

async function api(path, method = "GET", body = null) {
  try {
    const opts = { method, headers: { "Content-Type": "application/json" } };
    if (body) opts.body = JSON.stringify(body);
    const r = await fetch(`${CRM_URL}${path}`, opts);
    if (!r.ok) {
      let msg = `${r.status}`;
      try { const j = await r.json(); msg = j.detail || msg; } catch {}
      throw new Error(msg);
    }
    if (r.status === 204) return true;
    return await r.json();
  } catch (e) {
    console.error("Chairs API error:", path, e.message);
    return null;
  }
}

const inp = { width: "100%", padding: "6px 8px", border: `1px solid ${C.border}`, borderRadius: 4, fontSize: 13 };
const formLab = { fontSize: 11, color: C.grayDark, fontWeight: 600, marginBottom: 4, display: "block" };
const btnPri = { padding: "8px 14px", background: C.orange, color: C.white, border: "none", borderRadius: 4, cursor: "pointer", fontWeight: 600, fontSize: 13 };
const btnGhost = { padding: "8px 14px", background: C.white, color: C.green, border: `1px solid ${C.border}`, borderRadius: 4, cursor: "pointer", fontSize: 13 };
const btnDanger = { padding: "8px 14px", background: C.danger, color: C.white, border: "none", borderRadius: 4, cursor: "pointer", fontWeight: 600, fontSize: 13 };
const modalBg = { position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center" };
const modalBox = { background: C.white, borderRadius: 8, padding: 24, maxWidth: 700, width: "90%", maxHeight: "90vh", overflowY: "auto" };

// Google Places Autocomplete input. As you type, Google suggests real
// addresses / place names. On select, fires onPick({address, lat, lng, name}).
function PlacesAutocompleteInput({ value, onChange, onPick, placeholder, style }) {
  const inputRef = useRef(null);
  const acRef = useRef(null);
  const [loadErr, setLoadErr] = useState(false);

  useEffect(() => {
    let cancelled = false;
    loadGoogleMaps()
      .then((maps) => {
        if (cancelled || !inputRef.current || acRef.current) return;
        if (!maps.places || !maps.places.Autocomplete) {
          setLoadErr(true);
          return;
        }
        const ac = new maps.places.Autocomplete(inputRef.current, {
          fields: ["formatted_address", "geometry", "name"],
          types: ["geocode", "establishment"],
        });
        ac.addListener("place_changed", () => {
          const p = ac.getPlace();
          if (p && p.geometry && p.geometry.location) {
            onPick({
              address: p.formatted_address || p.name || "",
              lat: p.geometry.location.lat(),
              lng: p.geometry.location.lng(),
              name: p.name || "",
            });
          }
        });
        acRef.current = ac;
      })
      .catch(() => setLoadErr(true));
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <input
        ref={inputRef}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        style={style || inp}
        placeholder={placeholder || "Type a place name or address…"}
        autoComplete="off"
      />
      {loadErr && (
        <div style={{ fontSize: 11, color: C.danger, marginTop: 4 }}>
          Address suggestions unavailable. Enable Places API on the frontend Maps key.
        </div>
      )}
    </>
  );
}

// One-tap GPS. Uses browser geolocation — works on phone + desktop.
// Best UX when installer is physically next to the chair.
function UseCurrentLocationButton({ onLocate, label = "📍 Use my current location" }) {
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const click = () => {
    if (!navigator.geolocation) {
      setErr("GPS not supported on this device.");
      return;
    }
    setLoading(true); setErr("");
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setLoading(false);
        onLocate({
          lat: pos.coords.latitude,
          lng: pos.coords.longitude,
          accuracy: pos.coords.accuracy,
        });
      },
      (e) => {
        setLoading(false);
        if (e.code === 1) setErr("Location permission denied. Allow it in browser settings.");
        else if (e.code === 2) setErr("GPS unavailable. Try outdoors or near a window.");
        else if (e.code === 3) setErr("GPS timed out. Try again.");
        else setErr("Couldn't get GPS fix.");
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
    );
  };
  return (
    <div>
      <button type="button" onClick={click} disabled={loading} style={{ ...btnGhost, padding: "6px 12px", fontSize: 12 }}>
        {loading ? "Getting GPS..." : label}
      </button>
      {err && <div style={{ fontSize: 11, color: C.danger, marginTop: 4 }}>{err}</div>}
    </div>
  );
}

export default function ChairsScreen() {
  const [chairs, setChairs] = useState([]);
  const [stats, setStats] = useState(null);
  const [orgs, setOrgs] = useState([]);
  const [filter, setFilter] = useState({ status: "", org_id: "" });
  const [showNew, setShowNew] = useState(false);
  const [selected, setSelected] = useState(null);
  const [pocOrgId, setPocOrgId] = useState(null);
  const [certOrg, setCertOrg] = useState(null);
  const [view, setView] = useState("list");  // "list" | "map"

  const load = useCallback(async () => {
    const qs = new URLSearchParams({ admin_email: ADMIN_EMAIL });
    if (filter.status) qs.set("status", filter.status);
    if (filter.org_id) qs.set("org_id", filter.org_id);
    setChairs((await api(`/admin/chairs?${qs}`)) || []);
    setStats(await api(`/admin/chair-stats?admin_email=${ADMIN_EMAIL}`));
  }, [filter.status, filter.org_id]);

  useEffect(() => { api("/organizations?limit=500").then(o => setOrgs(o || [])); }, []);
  useEffect(() => { load(); }, [load]);

  const card = { padding: 14, background: C.card, borderRadius: 8, border: `1px solid ${C.border}` };
  const lbl = { fontSize: 11, color: C.grayMid, fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5 };
  const val = { fontSize: 28, fontWeight: 700, color: C.green, marginTop: 4 };

  return (
    <div style={{ padding: 24, background: C.cream, minHeight: "100vh" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
        <div>
          <h2 style={{ margin: 0, color: C.green, fontSize: 22 }}>Chair Tracking</h2>
          <div style={{ fontSize: 12, color: C.grayMid, marginTop: 4 }}>Outpost Evacuation · live registry · certified reporting</div>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <div style={{ display: "flex", border: `1px solid ${C.border}`, borderRadius: 4, overflow: "hidden" }}>
            <button onClick={() => setView("list")} style={{ padding: "8px 14px", border: "none", cursor: "pointer", fontWeight: 600, fontSize: 13, background: view === "list" ? C.green : C.white, color: view === "list" ? C.white : C.green }}>List</button>
            <button onClick={() => setView("map")} style={{ padding: "8px 14px", border: "none", cursor: "pointer", fontWeight: 600, fontSize: 13, background: view === "map" ? C.green : C.white, color: view === "map" ? C.white : C.green }}>Map</button>
          </div>
          <button onClick={() => setShowNew(true)} style={btnPri}>+ Add Chair</button>
        </div>
      </div>

      {stats && (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 12, marginBottom: 20 }}>
          <div style={card}><div style={lbl}>Total chairs</div><div style={val}>{stats.total_chairs}</div></div>
          <div style={card}><div style={lbl}>Inspections due (30d)</div>
            <div style={{ ...val, color: stats.inspections_due_30_days > 0 ? C.warning : C.green }}>{stats.inspections_due_30_days}</div>
          </div>
          <div style={card}><div style={lbl}>By status</div>
            <div style={{ fontSize: 12, marginTop: 4 }}>
              {(stats.by_status || []).slice(0, 4).map(x => (
                <div key={x.status} style={{ display: "flex", justifyContent: "space-between", padding: "2px 0" }}>
                  <span style={{ textTransform: "capitalize" }}>{(x.status || "").replace("_", " ")}</span>
                  <span style={{ fontWeight: 600 }}>{x.n}</span>
                </div>
              ))}
            </div>
          </div>
          <div style={card}><div style={lbl}>By model</div>
            <div style={{ fontSize: 12, marginTop: 4 }}>
              {(stats.by_model || []).slice(0, 4).map(m => (
                <div key={m.model} style={{ display: "flex", justifyContent: "space-between", padding: "2px 0" }}>
                  <span style={{ textTransform: "uppercase" }}>{m.model}</span>
                  <span style={{ fontWeight: 600 }}>{m.n}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {stats && stats.by_org && stats.by_org.length > 0 && (
        <div style={{ marginBottom: 20, padding: 14, background: C.card, borderRadius: 8, border: `1px solid ${C.border}` }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: C.green, marginBottom: 10, textTransform: "uppercase", letterSpacing: 0.5 }}>
            Chairs by customer · configure POC and send certified reports
          </div>
          {stats.by_org.map((o, i) => {
            const org = orgs.find(x => x.name === o.org_name);
            return (
              <div key={i} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 0", borderBottom: i === stats.by_org.length - 1 ? "none" : `1px solid ${C.light}` }}>
                <span style={{ color: C.green }}>{o.org_name} <span style={{ color: C.grayMid, fontSize: 11 }}>({o.n} chairs)</span></span>
                {org ? (
                  <div style={{ display: "flex", gap: 8 }}>
                    <button onClick={() => setPocOrgId(org.id)} style={{ ...btnGhost, padding: "4px 10px", fontSize: 11 }}>⚙️ POCs</button>
                    <button onClick={() => window.open(`${CRM_URL}/admin/chairs/cert-preview/${org.id}?admin_email=${ADMIN_EMAIL}`, "_blank")} style={{ ...btnGhost, padding: "4px 10px", fontSize: 11 }}>📄 Preview/Download</button>
                    <button onClick={() => setCertOrg(org)} style={{ ...btnPri, padding: "4px 10px", fontSize: 11 }}>📧 Send Certified Report</button>
                  </div>
                ) : (
                  <span style={{ fontSize: 11, color: C.grayMid, fontStyle: "italic" }}>internal inventory</span>
                )}
              </div>
            );
          })}
        </div>
      )}

      {view === "list" && (<div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
        <select value={filter.status} onChange={e => setFilter({ ...filter, status: e.target.value })} style={inp}>
          <option value="">All statuses</option>
          {CHAIR_STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        <select value={filter.org_id} onChange={e => setFilter({ ...filter, org_id: e.target.value })} style={inp}>
          <option value="">All orgs (incl. inventory)</option>
          {orgs.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
        </select>
      </div>)}

      {view === "map" && <ChairsMap chairs={chairs} onChairClick={(c) => setSelected(c)} />}

      {view === "list" && (<div style={{ overflowX: "auto", background: C.card, borderRadius: 8, border: `1px solid ${C.border}` }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
          <thead>
            <tr style={{ background: C.green, color: C.white }}>
              {["Serial","Model","Org","Location","Status","Last verified","Next inspection"].map(h =>
                <th key={h} style={{ padding: 10, textAlign: "left" }}>{h}</th>)}
            </tr>
          </thead>
          <tbody>
            {chairs.map(c => (
              <tr key={c.id} onClick={() => setSelected(c)} style={{ borderBottom: `1px solid ${C.light}`, cursor: "pointer" }}>
                <td style={{ padding: 10 }}>{c.halo_serial || "-"}</td>
                <td style={{ padding: 10, textTransform: "uppercase" }}>{c.model || "-"}</td>
                <td style={{ padding: 10 }}>{c.org_name || <span style={{ color: C.grayMid, fontStyle: "italic" }}>inventory</span>}</td>
                <td style={{ padding: 10, fontSize: 12 }}>{[c.building_name, c.floor, c.stairwell_or_room].filter(Boolean).join(" / ") || "-"}</td>
                <td style={{ padding: 10 }}>
                  <span style={{
                    padding: "2px 8px", borderRadius: 4, fontSize: 11, fontWeight: 600, color: C.white,
                    background: c.status === "in_place" ? C.success
                              : c.status === "missing" ? C.danger
                              : c.status === "maintenance" ? C.warning
                              : c.status === "moved" ? C.orange
                              : C.grayMid,
                  }}>{(c.status || "unknown").replace("_", " ")}</span>
                </td>
                <td style={{ padding: 10, fontSize: 12 }}>{c.last_verified_date || "-"}</td>
                <td style={{ padding: 10, fontSize: 12 }}>{c.next_inspection_due || "-"}</td>
              </tr>
            ))}
            {chairs.length === 0 && (<tr><td colSpan="7" style={{ padding: 24, textAlign: "center", color: C.grayMid }}>No chairs yet. Click "+ Add Chair" to register your first one.</td></tr>)}
          </tbody>
        </table>
      </div>)}

      {showNew && <ChairForm orgs={orgs} onClose={() => setShowNew(false)} onSaved={() => { setShowNew(false); load(); }} />}
      {selected && <ChairDetail chair={selected} onClose={() => setSelected(null)} onChanged={() => { setSelected(null); load(); }} />}
      {pocOrgId && <POCConfig orgId={pocOrgId} orgName={(orgs.find(o => o.id === pocOrgId) || {}).name} onClose={() => setPocOrgId(null)} />}
      {certOrg && <CertReport org={certOrg} onClose={() => setCertOrg(null)} onSent={() => { setCertOrg(null); load(); }} />}
    </div>
  );
}

function ChairForm({ orgs, onClose, onSaved }) {
  const [f, setF] = useState({ org_id: "", halo_serial: "", model: "swift", install_date: "", building_name: "", floor: "", stairwell_or_room: "", status: "inventory", notes: "", location_lat: "", location_lng: "" });
  const [saving, setSaving] = useState(false);
  const [searchAddr, setSearchAddr] = useState("");
  const [geoMsg, setGeoMsg] = useState("");
  const submit = async () => {
    setSaving(true);
    const payload = {
      ...f,
      org_id: f.org_id === "" ? null : parseInt(f.org_id),
      location_lat: f.location_lat === "" ? null : parseFloat(f.location_lat),
      location_lng: f.location_lng === "" ? null : parseFloat(f.location_lng),
    };
    const res = await api(`/admin/chairs?admin_email=${ADMIN_EMAIL}`, "POST", payload);
    setSaving(false);
    if (res) onSaved();
  };
  return (
    <div style={modalBg} onClick={onClose}>
      <div style={{ ...modalBox, maxWidth: 600 }} onClick={e => e.stopPropagation()}>
        <h3 style={{ margin: "0 0 16px 0", color: C.green }}>Add chair</h3>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
          <div><label style={formLab}>HALO serial</label><input value={f.halo_serial} onChange={e => setF({ ...f, halo_serial: e.target.value })} style={inp} /></div>
          <div><label style={formLab}>Model</label><select value={f.model} onChange={e => setF({ ...f, model: e.target.value })} style={inp}>{CHAIR_MODELS.map(m => <option key={m} value={m}>{m.toUpperCase()}</option>)}</select></div>
          <div><label style={formLab}>Customer (blank = inventory)</label><select value={f.org_id} onChange={e => setF({ ...f, org_id: e.target.value })} style={inp}><option value="">— inventory —</option>{orgs.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}</select></div>
          <div><label style={formLab}>Status</label><select value={f.status} onChange={e => setF({ ...f, status: e.target.value })} style={inp}>{CHAIR_STATUSES.map(s => <option key={s} value={s}>{s}</option>)}</select></div>
          <div><label style={formLab}>Install date</label><input type="date" value={f.install_date} onChange={e => setF({ ...f, install_date: e.target.value })} style={inp} /></div>
          <div><label style={formLab}>Building</label><input value={f.building_name} onChange={e => setF({ ...f, building_name: e.target.value })} style={inp} /></div>
          <div><label style={formLab}>Floor</label><input value={f.floor} onChange={e => setF({ ...f, floor: e.target.value })} style={inp} /></div>
          <div><label style={formLab}>Stairwell / room</label><input value={f.stairwell_or_room} onChange={e => setF({ ...f, stairwell_or_room: e.target.value })} style={inp} /></div>
          <div style={{ gridColumn: "1 / 3", padding: 10, background: C.cream, borderRadius: 6, border: `1px solid ${C.border}` }}>
            <label style={{ ...formLab, marginBottom: 8, display: "block" }}>Map location</label>
            <div style={{ marginBottom: 10 }}>
              <label style={{ ...formLab, fontSize: 10 }}>Search by place name or address</label>
              <PlacesAutocompleteInput
                value={searchAddr}
                onChange={setSearchAddr}
                onPick={({ address, lat, lng }) => {
                  setF({ ...f, location_lat: lat.toFixed(6), location_lng: lng.toFixed(6) });
                  setSearchAddr(address);
                  setGeoMsg(`✓ ${address}`);
                }}
                placeholder="e.g. Cedars-Sinai Medical Center, 8700 Beverly Blvd"
              />
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10, flexWrap: "wrap" }}>
              <UseCurrentLocationButton onLocate={({ lat, lng, accuracy }) => {
                setF({ ...f, location_lat: lat.toFixed(6), location_lng: lng.toFixed(6) });
                setGeoMsg(`✓ GPS fix (±${Math.round(accuracy)}m accuracy)`);
              }} />
              <span style={{ fontSize: 11, color: C.grayMid }}>← tap if you're standing at the chair</span>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
              <div><label style={formLab}>Latitude</label><input value={f.location_lat} onChange={e => setF({ ...f, location_lat: e.target.value })} style={inp} placeholder="34.075632" /></div>
              <div><label style={formLab}>Longitude</label><input value={f.location_lng} onChange={e => setF({ ...f, location_lng: e.target.value })} style={inp} placeholder="-118.380400" /></div>
            </div>
            {geoMsg && <div style={{ fontSize: 11, marginTop: 6, color: geoMsg.startsWith("✓") ? C.success : C.danger }}>{geoMsg}</div>}
          </div>
          <div style={{ gridColumn: "1 / 3" }}><label style={formLab}>Notes</label><textarea value={f.notes} onChange={e => setF({ ...f, notes: e.target.value })} style={{ ...inp, minHeight: 60 }} /></div>
        </div>
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 16 }}>
          <button onClick={onClose} style={btnGhost}>Cancel</button>
          <button onClick={submit} disabled={saving} style={btnPri}>{saving ? "Saving..." : "Save chair"}</button>
        </div>
      </div>
    </div>
  );
}

function ChairDetail({ chair, onClose, onChanged }) {
  const [detail, setDetail] = useState(null);
  const [moveForm, setMoveForm] = useState({ new_status: chair.status, new_location: "", notes: "" });
  const [showMove, setShowMove] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [showLoc, setShowLoc] = useState(false);
  useEffect(() => { api(`/admin/chairs/${chair.id}?admin_email=${ADMIN_EMAIL}`).then(setDetail); }, [chair.id]);
  const submitMove = async () => {
    await api(`/admin/chairs/${chair.id}/move?admin_email=${ADMIN_EMAIL}`, "POST", moveForm);
    onChanged();
  };
  const submitDelete = async () => {
    const res = await api(`/admin/chairs/${chair.id}?admin_email=${ADMIN_EMAIL}`, "DELETE");
    if (res !== null) onChanged();
  };
  return (
    <div style={modalBg} onClick={onClose}>
      <div style={modalBox} onClick={e => e.stopPropagation()}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 12 }}>
          <div>
            <div style={{ fontSize: 11, color: C.orange, fontWeight: 600, textTransform: "uppercase" }}>Chair #{chair.id}</div>
            <h3 style={{ margin: "4px 0 0 0", color: C.green }}>{chair.halo_serial || "(no serial)"} · <span style={{ textTransform: "uppercase" }}>{chair.model || "?"}</span></h3>
            <div style={{ fontSize: 12, color: C.grayMid, marginTop: 2 }}>{chair.org_name || "(internal inventory)"} · {[chair.building_name, chair.floor, chair.stairwell_or_room].filter(Boolean).join(" / ") || "no location set"}</div>
          </div>
          <button onClick={onClose} style={{ ...btnGhost, padding: "4px 8px" }}>✕</button>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, fontSize: 13, marginBottom: 12 }}>
          <div><strong>Status:</strong> {(chair.status || "-").replace("_", " ")}</div>
          <div><strong>Install date:</strong> {chair.install_date || "-"}</div>
          <div><strong>Last verified:</strong> {chair.last_verified_date || "-"}</div>
          <div><strong>Next inspection:</strong> {chair.next_inspection_due || "-"}</div>
          <div style={{ gridColumn: "1 / 3" }}><strong>QR token:</strong> <code style={{ fontSize: 11 }}>{chair.qr_token}</code></div>
          {chair.notes && <div style={{ gridColumn: "1 / 3" }}><strong>Notes:</strong> {chair.notes}</div>}
        </div>
        {!showMove && !confirmDelete && !showLoc && (
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <button onClick={() => setShowMove(true)} style={btnPri}>Log status / location change</button>
            <button onClick={() => setShowLoc(true)} style={btnGhost}>📍 Edit map location</button>
            <button onClick={() => setConfirmDelete(true)} style={btnDanger}>🗑 Delete chair</button>
          </div>
        )}
        {showLoc && <EditLocationInline chair={chair} onClose={() => setShowLoc(false)} onSaved={onChanged} />}
        {showMove && (
          <div style={{ padding: 12, background: C.cream, borderRadius: 6, border: `1px solid ${C.border}` }}>
            <div style={{ fontWeight: 600, marginBottom: 8, color: C.green }}>Log a movement / status change</div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
              <div><label style={formLab}>New status</label><select value={moveForm.new_status} onChange={e => setMoveForm({ ...moveForm, new_status: e.target.value })} style={inp}>{CHAIR_STATUSES.map(s => <option key={s} value={s}>{s}</option>)}</select></div>
              <div><label style={formLab}>New location (free-text)</label><input value={moveForm.new_location} onChange={e => setMoveForm({ ...moveForm, new_location: e.target.value })} style={inp} /></div>
              <div style={{ gridColumn: "1 / 3" }}><label style={formLab}>Notes</label><input value={moveForm.notes} onChange={e => setMoveForm({ ...moveForm, notes: e.target.value })} style={inp} /></div>
            </div>
            <div style={{ display: "flex", gap: 8, marginTop: 8, justifyContent: "flex-end" }}>
              <button onClick={() => setShowMove(false)} style={btnGhost}>Cancel</button>
              <button onClick={submitMove} style={btnPri}>Save change</button>
            </div>
          </div>
        )}
        {confirmDelete && (
          <div style={{ padding: 12, background: "#FFF5F5", borderRadius: 6, border: `1px solid ${C.danger}` }}>
            <div style={{ fontWeight: 600, marginBottom: 8, color: C.danger }}>⚠️ Delete this chair permanently?</div>
            <div style={{ fontSize: 12, color: C.grayDark, marginBottom: 12 }}>
              This removes the chair from the registry. The movement-history audit trail is preserved with a "deleted" entry. Cannot be undone — admin only.
            </div>
            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
              <button onClick={() => setConfirmDelete(false)} style={btnGhost}>Cancel</button>
              <button onClick={submitDelete} style={btnDanger}>Yes, delete</button>
            </div>
          </div>
        )}
        {detail && detail.movements && detail.movements.length > 0 && (
          <div style={{ marginTop: 16 }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: C.orange, marginBottom: 6, textTransform: "uppercase", letterSpacing: 0.5 }}>Movement history</div>
            <div style={{ maxHeight: 200, overflowY: "auto", fontSize: 12 }}>
              {detail.movements.map(m => (
                <div key={m.id} style={{ padding: "8px 0", borderBottom: `1px solid ${C.light}` }}>
                  <div style={{ color: C.grayMid, fontSize: 11 }}>{m.reported_at} · {m.source}</div>
                  <div>{m.old_status && m.old_status !== m.new_status && <><span style={{ color: C.grayMid }}>{m.old_status}</span> → </>}<strong>{m.new_status}</strong>{m.new_location && <> @ {m.new_location}</>}</div>
                  {m.notes && <div style={{ color: C.grayMid, fontSize: 11 }}>{m.notes}</div>}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function POCConfig({ orgId, orgName, onClose }) {
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    api(`/admin/chair-subscriptions/${orgId}?admin_email=${ADMIN_EMAIL}`).then(setS);
  }, [orgId]);
  const save = async () => {
    setSaving(true);
    await api(`/admin/chair-subscriptions/${orgId}?admin_email=${ADMIN_EMAIL}`, "POST", s);
    setSaving(false);
    onClose();
  };
  if (!s) return null;
  return (
    <div style={modalBg} onClick={onClose}>
      <div style={{ ...modalBox, maxWidth: 540 }} onClick={e => e.stopPropagation()}>
        <h3 style={{ margin: "0 0 4px 0", color: C.green }}>Configure chair-tracking POCs</h3>
        <div style={{ fontSize: 12, color: C.grayMid, marginBottom: 16 }}>{orgName} · up to 2 emails will receive certified reports</div>
        <div style={{ display: "grid", gap: 10 }}>
          <div><label style={formLab}>Primary POC email *</label><input value={s.poc_email_1 || ""} onChange={e => setS({ ...s, poc_email_1: e.target.value })} style={inp} placeholder="primary@cedars-sinai.org" /></div>
          <div><label style={formLab}>Secondary POC email (optional)</label><input value={s.poc_email_2 || ""} onChange={e => setS({ ...s, poc_email_2: e.target.value })} style={inp} placeholder="backup@cedars-sinai.org" /></div>
          <div style={{ borderTop: `1px solid ${C.light}`, paddingTop: 10, marginTop: 6 }}>
            <div style={{ fontSize: 11, color: C.orange, fontWeight: 600, textTransform: "uppercase", marginBottom: 8 }}>Default certifier (Outpost side)</div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
              <div><label style={formLab}>Name</label><input value={s.default_certifier_name || ""} onChange={e => setS({ ...s, default_certifier_name: e.target.value })} style={inp} /></div>
              <div><label style={formLab}>Email</label><input value={s.default_certifier_email || ""} onChange={e => setS({ ...s, default_certifier_email: e.target.value })} style={inp} /></div>
              <div style={{ gridColumn: "1 / 3" }}><label style={formLab}>Title</label><input value={s.default_certifier_title || ""} onChange={e => setS({ ...s, default_certifier_title: e.target.value })} style={inp} /></div>
            </div>
          </div>
        </div>
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 16 }}>
          <button onClick={onClose} style={btnGhost}>Cancel</button>
          <button onClick={save} disabled={saving} style={btnPri}>{saving ? "Saving..." : "Save POCs"}</button>
        </div>
      </div>
    </div>
  );
}

function CertReport({ org, onClose, onSent }) {
  const [sub, setSub] = useState(null);
  const [certifier, setCertifier] = useState({ name: "", email: "", title: "" });
  const [extra, setExtra] = useState("");
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    api(`/admin/chair-subscriptions/${org.id}?admin_email=${ADMIN_EMAIL}`).then(s => {
      setSub(s);
      setCertifier({
        name:  s?.default_certifier_name  || "Drew Jennings",
        email: s?.default_certifier_email || "drew@outpostprep.com",
        title: s?.default_certifier_title || "Founder, Outpost Preparedness LLC",
      });
    });
  }, [org.id]);

  const send = async () => {
    setSending(true);
    const extras = extra.split(",").map(s => s.trim()).filter(Boolean);
    const r = await api(`/admin/chairs/cert-report/${org.id}?admin_email=${ADMIN_EMAIL}`, "POST", {
      certifier_name: certifier.name,
      certifier_email: certifier.email,
      certifier_title: certifier.title,
      extra_recipients: extras,
    });
    setSending(false);
    setResult(r);
    if (r && r.ok) setTimeout(onSent, 1800);
  };

  if (!sub) return null;
  const pocs = [sub.poc_email_1, sub.poc_email_2].filter(Boolean);
  const hasPocs = pocs.length > 0;

  return (
    <div style={modalBg} onClick={onClose}>
      <div style={{ ...modalBox, maxWidth: 600 }} onClick={e => e.stopPropagation()}>
        <div style={{ fontSize: 11, color: C.orange, fontWeight: 600, textTransform: "uppercase" }}>Certified report</div>
        <h3 style={{ margin: "4px 0 4px 0", color: C.green }}>Send to {org.name}</h3>
        <div style={{ fontSize: 12, color: C.grayMid, marginBottom: 16 }}>Branded compliance PDF, digitally signed, emailed with read-receipt request.</div>

        {!hasPocs ? (
          <div style={{ padding: 12, background: "#FFF5F5", borderRadius: 6, border: `1px solid ${C.danger}` }}>
            <div style={{ fontWeight: 600, color: C.danger, marginBottom: 6 }}>⚠️ No POC emails on file</div>
            <div style={{ fontSize: 12, color: C.grayDark }}>Add at least one POC email under "Configure POCs" before sending a certified report. Reports go only to the per-customer POCs — never via direct download.</div>
            <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 10 }}>
              <button onClick={onClose} style={btnGhost}>Close</button>
            </div>
          </div>
        ) : (
          <>
            <div style={{ padding: 12, background: C.cream, borderRadius: 6, marginBottom: 14, border: `1px solid ${C.border}` }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: C.green, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 6 }}>Will send to</div>
              {pocs.map(e => <div key={e} style={{ fontSize: 13 }}>📨 {e}</div>)}
              <div style={{ fontSize: 11, color: C.grayMid, marginTop: 6 }}>Read-receipt request will be sent to {certifier.email}.</div>
            </div>

            <div style={{ fontSize: 11, fontWeight: 600, color: C.orange, textTransform: "uppercase", marginBottom: 8 }}>Certified by</div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 12 }}>
              <div><label style={formLab}>Name</label><input value={certifier.name} onChange={e => setCertifier({ ...certifier, name: e.target.value })} style={inp} /></div>
              <div><label style={formLab}>Email (signature)</label><input value={certifier.email} onChange={e => setCertifier({ ...certifier, email: e.target.value })} style={inp} /></div>
              <div style={{ gridColumn: "1 / 3" }}><label style={formLab}>Title</label><input value={certifier.title} onChange={e => setCertifier({ ...certifier, title: e.target.value })} style={inp} /></div>
            </div>
            <div style={{ marginBottom: 14 }}>
              <label style={formLab}>Optional CC (comma-separated emails)</label>
              <input value={extra} onChange={e => setExtra(e.target.value)} style={inp} placeholder="ops@cedars-sinai.org, surveyor@..." />
            </div>
            {result && (
              <div style={{ padding: 10, background: result.ok ? "#E8F5E9" : "#FFF5F5", borderRadius: 6, marginBottom: 12, fontSize: 12, color: result.ok ? C.success : C.danger }}>
                {result.ok ? (
                  <>
                    <div>✓ Sent to {result.sent_to.join(", ")}</div>
                    {result.kb_doc_id && <div style={{ fontSize: 11, marginTop: 4 }}>📁 Filed to Knowledge Base (doc #{result.kb_doc_id})</div>}
                    {result.cert_log_id && (
                      <a href={`${CRM_URL}/admin/chair-cert-pdf/${result.cert_log_id}?admin_email=${ADMIN_EMAIL}`} target="_blank" rel="noopener noreferrer" style={{ display: "inline-block", marginTop: 6, color: C.green, fontWeight: 600 }}>
                        📄 Download a copy of the sent PDF →
                      </a>
                    )}
                  </>
                ) : `✗ Send failed`}
              </div>
            )}
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
              <button onClick={onClose} style={btnGhost} disabled={sending}>Cancel</button>
              <button onClick={send} disabled={sending || !certifier.name || !certifier.email} style={btnPri}>
                {sending ? "Sending..." : "📧 Send certified report"}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function EditLocationInline({ chair, onClose, onSaved }) {
  const [lat, setLat] = useState(chair.location_lat ?? "");
  const [lng, setLng] = useState(chair.location_lng ?? "");
  const [building, setBuilding] = useState(chair.building_name || "");
  const [searchAddr, setSearchAddr] = useState("");
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState("");

  const save = async () => {
    setSaving(true);
    const payload = {
      building_name: building || null,
      location_lat: lat === "" ? null : parseFloat(lat),
      location_lng: lng === "" ? null : parseFloat(lng),
    };
    const r = await api(`/admin/chairs/${chair.id}?admin_email=${ADMIN_EMAIL}`, "PATCH", payload);
    setSaving(false);
    if (r) onSaved();
  };

  return (
    <div style={{ padding: 12, background: C.cream, borderRadius: 6, border: `1px solid ${C.border}` }}>
      <div style={{ fontWeight: 600, marginBottom: 8, color: C.green }}>Edit map location</div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
        <div style={{ gridColumn: "1 / 3" }}>
          <label style={formLab}>Building (label only — your name for it)</label>
          <input value={building} onChange={e => setBuilding(e.target.value)} style={inp} placeholder="e.g. Tower 2, Main Hospital" />
        </div>
        <div style={{ gridColumn: "1 / 3" }}>
          <label style={formLab}>Search by place name or address</label>
          <PlacesAutocompleteInput
            value={searchAddr}
            onChange={setSearchAddr}
            onPick={({ address, lat: la, lng: ln }) => {
              setLat(la.toFixed(6));
              setLng(ln.toFixed(6));
              setSearchAddr(address);
              setMsg(`✓ ${address}`);
            }}
            placeholder="e.g. Cedars-Sinai Medical Center, 8700 Beverly Blvd"
          />
        </div>
        <div style={{ gridColumn: "1 / 3", display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
          <UseCurrentLocationButton onLocate={({ lat: la, lng: ln, accuracy }) => {
            setLat(la.toFixed(6));
            setLng(ln.toFixed(6));
            setMsg(`✓ GPS fix (±${Math.round(accuracy)}m accuracy)`);
          }} />
          <span style={{ fontSize: 11, color: C.grayMid }}>← tap if you're standing at the chair</span>
        </div>
        <div><label style={formLab}>Latitude</label><input value={lat} onChange={e => setLat(e.target.value)} style={inp} /></div>
        <div><label style={formLab}>Longitude</label><input value={lng} onChange={e => setLng(e.target.value)} style={inp} /></div>
      </div>
      {msg && <div style={{ fontSize: 11, marginTop: 6, color: msg.startsWith("✓") ? C.success : C.danger }}>{msg}</div>}
      <div style={{ display: "flex", gap: 8, marginTop: 10, justifyContent: "flex-end" }}>
        <button onClick={onClose} style={btnGhost}>Cancel</button>
        <button onClick={save} disabled={saving} style={btnPri}>{saving ? "Saving..." : "Save location"}</button>
      </div>
    </div>
  );
}
